//#region IMPORT
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { fetchHwpBytes, loadHwpEngine } from './hwpEngine';
//#endregion

const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 3;

/**
 * 한글(hwp/hwpx) 파일 미리보기 컴포넌트
 * rhwp(WASM)로 원본을 직접 파싱해 페이지별 SVG로 렌더링한다.
 * @param {string} props.filePath - 파일 경로 또는 전체 URL (내부에서 상대 경로로 정규화)
 * @param {string} props.fileName - 파일명 (확장자 포함)
 * @param {string} [props.height='600px'] - 미리보기 영역 높이
 * @param {Function} [props.onError] - 파싱 실패 시 호출 (PDF 폴백 등에 사용)
 * @returns {JSX.Element} 미리보기 컴포넌트
 */
function HwpPreview({ filePath, fileName, height = '600px', onError }) {
  //#region 변수
  const docRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [svg, setSvg] = useState('');
  const [zoom, setZoom] = useState(1);
  //#endregion

  //#region 나머지 함수
  const releaseDoc = useCallback(() => {
    try {
      docRef.current?.free?.();
    } catch (e) {
      // 이미 해제된 문서는 무시
    }
    docRef.current = null;
  }, []);
  //#endregion

  //#region useEffect
  // 파일이 바뀌면 원본을 내려받아 문서를 파싱
  useEffect(() => {
    let cancelled = false;

    const loadDocument = async () => {
      setStatus('loading');
      setErrorMsg('');
      setSvg('');
      setPageIndex(0);
      setZoom(1);

      try {
        const [{ HwpDocument }, bytes] = await Promise.all([
          loadHwpEngine(),
          fetchHwpBytes(filePath, fileName),
        ]);

        if (cancelled) return;

        releaseDoc();
        const doc = new HwpDocument(bytes);
        docRef.current = doc;

        setPageCount(doc.pageCount());
        setStatus('ready');
      } catch (e) {
        if (cancelled) return;

        console.error('HwpPreview: 한글 파일 미리보기 실패', e);
        setErrorMsg(e?.message || '한글 파일을 열 수 없습니다.');
        setStatus('error');
        onError?.(e);
      }
    };

    if (filePath) {
      loadDocument();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, fileName]);

  // 언마운트 시 WASM 메모리 해제
  useEffect(() => {
    return () => releaseDoc();
  }, [releaseDoc]);

  // 현재 페이지 렌더링
  useEffect(() => {
    if (status !== 'ready' || !docRef.current) return;

    try {
      setSvg(docRef.current.renderPageSvg(pageIndex));
    } catch (e) {
      console.error('HwpPreview: 페이지 렌더링 실패', e);
      setErrorMsg(e?.message || '페이지를 렌더링할 수 없습니다.');
      setStatus('error');
      onError?.(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, pageIndex]);
  //#endregion

  if (status === 'loading') {
    return (
      <Box
        sx={{
          width: '100%',
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          한글 문서를 불러오는 중입니다...
        </Typography>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box
        sx={{
          width: '100%',
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Typography variant="body2" color="error" textAlign="center">
          {errorMsg}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          disabled={pageIndex <= 0}
          onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
        >
          <NavigateBeforeIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" sx={{ minWidth: 64, textAlign: 'center' }}>
          {pageIndex + 1} / {pageCount}
        </Typography>
        <IconButton
          size="small"
          disabled={pageIndex >= pageCount - 1}
          onClick={() =>
            setPageIndex((prev) => Math.min(pageCount - 1, prev + 1))
          }
        >
          <NavigateNextIcon fontSize="small" />
        </IconButton>

        <Box sx={{ width: 16 }} />

        <IconButton
          size="small"
          disabled={zoom <= ZOOM_MIN}
          onClick={() => setZoom((prev) => Math.max(ZOOM_MIN, prev - ZOOM_STEP))}
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <IconButton
          size="small"
          disabled={zoom >= ZOOM_MAX}
          onClick={() => setZoom((prev) => Math.min(ZOOM_MAX, prev + ZOOM_STEP))}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: 'auto',
          backgroundColor: 'grey.200',
          p: 1,
        }}
      >
        <Box
          sx={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top center',
            '& svg': {
              display: 'block',
              margin: '0 auto',
              backgroundColor: '#fff',
              boxShadow: 1,
            },
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </Box>
    </Box>
  );
}

export default HwpPreview;
